import React, { useEffect, useState, useCallback } from "react";
import { ClipboardList, ChevronDown, CircleCheck, Loader2, Radio, RefreshCw, Wrench, Clock } from "lucide-react";
import api from "@/lib/api";
import { fmtNum, fmtFecha } from "@/lib/format";

const ESTADO_META = {
  "RESUELTO": { label: "RESUELTO", color: "#4ECDC4", bg: "bg-[#4ECDC4]/10", border: "border-[#4ECDC4]/40", icon: CircleCheck },
  "EN PROCESO": { label: "EN PROCESO", color: "#F39C12", bg: "bg-[#F39C12]/10", border: "border-[#F39C12]/40", icon: Loader2 },
};

const FILTROS = [
  { key: "", label: "Todas" },
  { key: "EN PROCESO", label: "En proceso" },
  { key: "RESUELTO", label: "Resueltas" },
];

const Cartel = ({ estado }) => {
  const m = ESTADO_META[estado] || { label: estado || "—", color: "#71717A", bg: "bg-white/5", border: "border-[#1F1F24]", icon: Radio };
  const Icon = m.icon;
  const spin = estado === "EN PROCESO" ? "animate-spin" : "";
  return (
    <span data-testid={`cartel-${estado}`} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider border ${m.bg} ${m.border}`} style={{ color: m.color }}>
      <Icon className={`w-3.5 h-3.5 ${spin}`} /> {m.label}
    </span>
  );
};

const StatCard = ({ label, value, color, testid }) => (
  <div data-testid={testid} className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] p-5 flex flex-col gap-2">
    <span className="text-[11px] text-[#71717A] uppercase tracking-[0.2em]">{label}</span>
    <span className="font-mono-ax text-4xl" style={{ color }}>{fmtNum(value)}</span>
  </div>
);

const Bitacora = () => {
  const [data, setData] = useState(null);
  const [pend, setPend] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [abierto, setAbierto] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const url = `/comando/bitacora${filtro ? `?estado=${encodeURIComponent(filtro)}` : ""}`;
      const { data } = await api.get(url);
      setPend(!!data?.puente_pendiente);
      setData(data);
    } catch (e) { /* noop */ }
    finally { setCargando(false); }
  }, [filtro]);

  useEffect(() => { document.title = "Bitácora · AXIORA"; }, []);

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 60000);
    return () => clearInterval(t);
  }, [cargar]);

  const incidencias = data?.incidencias || [];

  return (
    <div className="p-6 pb-32">
      <header className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="font-serif-ax text-4xl text-[#F5F5F7] tracking-wide flex items-center gap-3">
            <ClipboardList className="w-7 h-7 text-[#D4AF37]" /> Bitácora de Incidencias
          </h2>
          <p className="text-xs text-[#71717A] mt-0.5">Sucesos por juego · acciones de reparación · estado en vivo — vía puente</p>
        </div>
        <button data-testid="bitacora-refresh" onClick={cargar} disabled={cargando}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#1F1F24] text-[#A1A1AA] text-sm hover:border-[#4ECDC4]/40 hover:text-[#4ECDC4] disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${cargando ? "animate-spin" : ""}`} /> Actualizar
        </button>
      </header>

      {pend && (
        <div data-testid="bitacora-pendiente" className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-[#F39C12]/10 border border-[#F39C12]/30 text-[#F39C12] text-sm">
          <Radio className="w-4 h-4" /> PUENTE PENDIENTE — la bitácora no está disponible ahora.
        </div>
      )}

      {/* Contadores */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <StatCard testid="stat-total" label="Total" value={data?.total} color="#D4AF37" />
        <StatCard testid="stat-en-proceso" label="En proceso" value={data?.en_proceso} color="#F39C12" />
        <StatCard testid="stat-resueltas" label="Resueltas" value={data?.resueltas} color="#4ECDC4" />
      </div>

      {/* Filtro */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {FILTROS.map((f) => (
          <button key={f.key || "todas"} data-testid={`filtro-${f.key ? f.key.replace(" ", "-").toLowerCase() : "todas"}`}
            onClick={() => { setFiltro(f.key); setAbierto(null); }}
            className={`px-4 py-2 rounded-lg text-sm border ${filtro === f.key ? "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#1F1F24] text-[#71717A] hover:text-[#F5F5F7]"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Lista de incidencias */}
      <div className="space-y-3">
        {incidencias.length ? incidencias.map((inc, i) => {
          const rowId = `${inc.game_id}-${inc.abierta_at}-${i}`;
          const isOpen = abierto === rowId;
          const acciones = inc.acciones || [];
          return (
            <div key={rowId} data-testid={`incidencia-${i}`} className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] overflow-hidden">
              <button data-testid={`incidencia-toggle-${i}`} onClick={(e) => {
                  setAbierto(isOpen ? null : rowId);
                  if (!isOpen) {
                    const card = e.currentTarget.closest(`[data-testid="incidencia-${i}"]`);
                    setTimeout(() => card?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 120);
                  }
                }}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 hover:bg-white/[0.02] text-left">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[#F5F5F7] text-sm font-medium">{inc.nombre || inc.game_id}</span>
                    <span className="font-mono-ax text-[10px] text-[#52525b]">{inc.game_id}</span>
                  </div>
                  <p className="text-[#A1A1AA] text-xs mt-0.5 truncate">{inc.suceso}</p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <Cartel estado={inc.estado} />
                  <span className="font-mono-ax text-[10px] text-[#52525b] hidden md:inline">{fmtFecha(inc.abierta_at)}</span>
                  <ChevronDown className={`w-4 h-4 text-[#71717A] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
              </button>

              {isOpen && (
                <div className="px-5 pb-5 border-t border-[#1F1F24] pt-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
                    <div><span className="text-[#71717A] block uppercase tracking-wider text-[10px]">Intentos</span><span className="text-[#F5F5F7] font-mono-ax">{fmtNum(inc.intentos)}</span></div>
                    <div><span className="text-[#71717A] block uppercase tracking-wider text-[10px]">Alertada</span><span className="text-[#F5F5F7]">{inc.alertada ? "Sí" : "No"}</span></div>
                    <div><span className="text-[#71717A] block uppercase tracking-wider text-[10px]">Abierta</span><span className="text-[#A1A1AA] font-mono-ax text-[11px]">{fmtFecha(inc.abierta_at)}</span></div>
                    <div><span className="text-[#71717A] block uppercase tracking-wider text-[10px]">Resuelta</span><span className="text-[#A1A1AA] font-mono-ax text-[11px]">{inc.resuelto_at ? fmtFecha(inc.resuelto_at) : "—"}</span></div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-[#D4AF37] uppercase tracking-wider mb-2">
                    <Wrench className="w-3.5 h-3.5" /> Acciones ({acciones.length})
                  </div>
                  {acciones.length ? (
                    <div className="space-y-2">
                      {acciones.map((a, j) => (
                        <div key={j} className="rounded-lg bg-[#050505] border border-[#1F1F24] px-4 py-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Clock className="w-3 h-3 text-[#52525b]" />
                            <span className="font-mono-ax text-[10px] text-[#52525b]">{fmtFecha(a.ts)}</span>
                          </div>
                          <p className="text-[#F5F5F7] text-sm">{a.accion}</p>
                          {a.resultado && <p className="text-[#4ECDC4] text-xs mt-0.5">→ {a.resultado}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#52525b] text-sm">Sin acciones registradas.</p>
                  )}
                </div>
              )}
            </div>
          );
        }) : (
          <div className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] px-5 py-10 text-center text-[#52525b] text-sm">
            {pend ? "PUENTE PENDIENTE" : "No hay incidencias con el filtro actual."}
          </div>
        )}
      </div>
    </div>
  );
};

export default Bitacora;
